import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import PricingSection from '@/components/layout/PricingSection';
import SelfServePricing from '@/components/landing/SelfServePricing';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import SEO from '@/components/common/SEO';
import SimpleEnterpriseContactForm from '@/components/enterprise/SimpleEnterpriseContactForm';
import { toast } from '@/hooks/use-toast';
import { getMarketingServiceById } from '@/lib/marketing';

const Purchase = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);

  const serviceParam = new URLSearchParams(location.search).get('service');
  const selectedService = getMarketingServiceById(serviceParam);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('success') === 'true') {
      toast({ title: 'Request received', description: 'Our team will follow up shortly.' });
      navigate('/purchase', { replace: true });
    } else if (searchParams.get('canceled') === 'true') {
      toast({
        title: 'Action cancelled',
        description: 'No changes were made.',
        variant: 'default',
      });
      navigate('/purchase', { replace: true });
    }
  }, [location.search, navigate]);

  return (
    <MainLayout>
      <SEO
        title={selectedService ? `${selectedService.name} Request` : 'Sovereign AI Pricing & Services'}
        description={
          selectedService
            ? `Start the scoped engagement process for ${selectedService.name} with CanXP AI.`
            : 'Self-serve sovereign AI training plans and enterprise sovereign AI services from CanXP AI.'
        }
      />

      <section className="px-4 pt-12 pb-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              Pricing & services
            </Badge>
            <h1 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
              Sovereign AI, priced two ways.
            </h1>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              Launch self-serve training runs in minutes, or scope a sovereign AI engagement with our
              engineering team. Both paths run on Canadian-hosted sovereign infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {selectedService ? (
        <section className="px-4 py-8">
          <div className="container mx-auto max-w-5xl">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background shadow-[0_18px_60px_-40px_rgba(220,38,38,0.6)]">
              <CardHeader>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <Badge className="w-fit">Scoped service purchase</Badge>
                    <CardTitle className="mt-4 text-3xl tracking-tight">{selectedService.name}</CardTitle>
                    <CardDescription className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">
                      {selectedService.description}
                    </CardDescription>
                  </div>
                  <div className="rounded-2xl border border-primary/20 bg-background/70 p-5 text-left md:min-w-[280px]">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      Starting scope
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-primary">{selectedService.price}</p>
                    {selectedService.priceDetail ? (
                      <p className="mt-2 text-sm text-foreground/80">{selectedService.priceDetail}</p>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-8 lg:grid-cols-[1fr_0.8fr]">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    What happens next
                  </p>
                  <ul className="mt-4 space-y-3">
                    {selectedService.inclusions.slice(0, 4).map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-6 text-foreground/90">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-border/70 bg-background/80 p-5">
                  <p className="text-sm leading-7 text-muted-foreground">
                    This service starts with a scoped purchase intake so CanXP AI can confirm dataset readiness,
                    delivery timeline, deployment requirements, and final commercial terms before kickoff.
                  </p>
                  <div className="mt-6 flex flex-col gap-3">
                    <Button onClick={() => setIsEnterpriseModalOpen(true)} className="gap-2">
                      Start Scoped Service Request
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/enterprise')}>

                      Need a larger deployment?
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      ) : null}

      {/* Self-Serve pricing (tiers + add-ons) */}
      <SelfServePricing />

      {/* Existing Enterprise pricing block */}
      <PricingSection />

      {/* Enterprise contact CTA */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-6xl rounded-[2rem] border border-border/70 bg-background/80 p-8 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.5)] md:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Discuss your deployment
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Need something beyond self-serve?
              </h2>
              <p className="mt-5 max-w-3xl text-base leading-7 text-muted-foreground md:text-lg">
                For multi-stakeholder programs, regulated deployments, frontier model development, or sovereign
                hosting, our enterprise team will architect the right engagement with you.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => setIsEnterpriseModalOpen(true)} size="lg" className="gap-2">
                  Contact Enterprise Sales
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="lg" onClick={() => navigate('/enterprise')}>
                  Request Architecture Review
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={isEnterpriseModalOpen} onOpenChange={setIsEnterpriseModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedService ? `Request ${selectedService.name}` : 'Contact Enterprise Sales'}
            </DialogTitle>
            <DialogDescription>
              {selectedService
                ? 'Share your organization details and our team will confirm scope, timeline, and next steps for this service package.'
                : 'Fill out the form below and our team will get in touch with you shortly.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <SimpleEnterpriseContactForm onSuccess={() => setIsEnterpriseModalOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Purchase;
